import {useState} from 'react'
import {useNavigate} from 'react-router'
import {faDownload} from '@fortawesome/free-solid-svg-icons'
import invariant from 'invariant'
import isEqual from 'lodash/isEqual'
import {exhaustive} from '@freckle/exhaustive'
import {useSafeCallback, useSafeCallbackExtraDeps} from '@freckle/react-hooks'
import {useSafeEffect, useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {DelayedSpinner} from '@freckle/educator-entities/ts/common/components/spinner-wrapper/delayed-spinner'
import {HttpError} from '@freckle/educator-entities/ts/common/exceptions/http-error'
import type {OperationLocationT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {equalExtra, staticExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import {useOperationPersistence} from '@freckle/educator-entities/ts/common/hooks/operations/use-operation-persistence'
import {spanDangerous} from '@freckle/educator-entities/ts/common/models/html-text'
import {
  downloadFile,
  extractDataAndHeaders,
  fancyClient,
} from '@freckle/educator-entities/ts/openapi-clients'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {getActiveIdps} from '@freckle/educator-entities/ts/roster/models/school'
import {Alert} from '@freckle/educator-materials/ts/components/alert'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SafeTrans} from '@freckle/educator-materials/ts/helpers/translate/component'
import {PageWrapper} from '@freckle/school/ts/common/components/common/page-wrapper'
import {PendingOperationWhenLocation} from '@freckle/school/ts/common/components/common/pending-operation'
import RosterApi from '@freckle/school/ts/common/helpers/api/rostering'
import {logErrorAsUnhandled} from '@freckle/school/ts/common/helpers/exception-handlers/bugsnag-client'
import Notification from '@freckle/school/ts/common/helpers/notification'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import {fetchSummary} from '@freckle/school/ts/common/models/summary'
import type {UnmanagedCourseT} from '@freckle/school/ts/common/models/unmanaged-course'
import {Routes} from '@freckle/school/ts/common/routers/routes'
import {ArchiveCourses} from '@freckle/school/ts/rostering/components/clean-up-roster/archive-courses'
import type {CleanUpPhaseT} from '@freckle/school/ts/rostering/components/clean-up-roster/clean-up-phase'
import {calculateUnmanagedStudents} from '@freckle/school/ts/rostering/components/clean-up-roster/clean-up-phase'
import {CleanUpRoster} from '@freckle/school/ts/rostering/components/clean-up-roster/clean-up-roster'
import {CourseSummaryModal} from '@freckle/school/ts/rostering/components/clean-up-roster/course-summary-modal'
import {NoCoursesFound} from '@freckle/school/ts/rostering/components/clean-up-roster/no-courses-found'
import ConfirmMergeModal from '@freckle/school/ts/rostering/components/common/confirm-merge-modal'
import ExitModal from '@freckle/school/ts/rostering/components/common/exit-modal'
import {FinishedMerging} from '@freckle/school/ts/rostering/components/common/finished-merging'
import RosterBody from '@freckle/school/ts/rostering/components/common/roster-body'
import type {SchoolAdminAttrs} from '@freckle/school/ts/users/models/school-admin'
import {
  teachersMissingEmailAlertWrapper,
  pageWrapper,
  downloadLink,
} from './clean-up-roster-container.module.scss'

type Props = {
  schools: Array<SchoolAttrs>
  schoolAdmin: SchoolAdminAttrs
  initialSummary: SummaryT
  numSelfRosteredTeachers: number
  initialUnmanagedCourses: Array<UnmanagedCourseT>
}

type RosterModalT = 'exit-early' | 'course-summary' | 'confirm-dedup-merge'

const DUPLICATE_STUDENTS_KEY = 'merge-duplicate-students'

export function CleanUpRosterContainer(props: Props) {
  const {t} = useSafeTranslation()
  const {initialSummary, schools, numSelfRosteredTeachers, initialUnmanagedCourses, schoolAdmin} =
    props

  const [summary, setSummary] = useState(initialSummary)
  const [phase, setPhase] = useState<CleanUpPhaseT>({tag: 'start'})
  const [cleanUpCourses, setCleanUpCourses] = useState(false)
  const [modal, setModal] = useState<RosterModalT | null>(null)
  const [cancel, setCancel] = useState(false)
  const operationPersistence = useOperationPersistence()
  const [operationLocation, setOperationLocation] = useState<OperationLocationT | null>(
    operationPersistence.pendingLocationFor(DUPLICATE_STUDENTS_KEY),
  )
  const navigate = useNavigate()

  const mergeStudentsError = t('MERGE_STUDENTS_ERROR')

  const onMergeDuplicateStudentsFailure = useSafeCallback(
    () => () => {
      setOperationLocation(null)
      Notification.displayErrorNotification(mergeStudentsError)
      setPhase({tag: 'start'})
    },
    [mergeStudentsError],
  )

  const rebuildSummaryAndMoveToNextPhase = useSafeCallbackExtraDeps<
    () => void,
    {schools: Array<SchoolAttrs>}
  >(
    ({schools}) =>
      async () => {
        const schoolIds = schools.map(school => school.id)

        try {
          const newSummary = await fetchSummary(schoolAdmin.id, schoolIds)
          setSummary(newSummary)
          const unmanaged = calculateUnmanagedStudents(schools, newSummary)
          if (cleanUpCourses) {
            setPhase({tag: 'scanning-courses', unmanaged})
            const remaining = await RosterApi.findUnmanagedCourses(schoolIds)
            if (remaining.length === 0) {
              setPhase({tag: 'no-courses-found', unmanaged})
            } else {
              setPhase({tag: 'finished-scanning', unmanaged, remaining})
            }
          } else {
            setPhase({tag: 'finished-merging', unmanaged})
          }
        } catch (e) {
          logErrorAsUnhandled(new HttpError(e))
          Notification.displayErrorNotification(
            'Could not merge duplicate students. Please try again later.',
          )
          setPhase({tag: 'start'})
        }
      },
    [cleanUpCourses, schoolAdmin.id],
    {
      schools: {value: schools, comparator: isEqual},
    },
  )

  const onMergeDuplicateStudentsFinish = useSafeCallbackExtraDeps(
    ({persistence}: {persistence: typeof operationPersistence}) =>
      () =>
        persistence.remove(DUPLICATE_STUDENTS_KEY),

    [],
    {
      persistence: {value: operationPersistence, comparator: () => true},
    },
  )

  const onMergeDuplicateStudentsSuccess = useSafeCallbackExtraDeps<
    () => void,
    {rebuildSummaryAndMoveToNextPhase: () => void}
  >(
    ({rebuildSummaryAndMoveToNextPhase}) =>
      () => {
        setOperationLocation(null)
        rebuildSummaryAndMoveToNextPhase()
      },
    [],
    {
      rebuildSummaryAndMoveToNextPhase,
    },
  )

  useSafeEffect(
    () => () =>
      // Cancel pending requests when component unmounts
      setCancel(true),
    [],
  )

  const onMerge = () => setModal('confirm-dedup-merge')

  const onMergeConfirmed = async () => {
    setPhase({tag: 'merging-duplicates'})
    setModal(null)
  }

  const onReview = () => setModal('course-summary')

  const onArchive = async () => {
    if (phase.tag === 'finished-scanning') {
      const {remaining, unmanaged} = phase

      setPhase({tag: 'start-archiving-courses', unmanaged, archived: [], remaining})
    }
  }

  const onFinish = () => {
    if (phase.tag === 'finished-scanning' && modal !== 'exit-early') {
      setModal('exit-early')
      return
    }

    if (phase.tag === 'finished-merging' || phase.tag === 'finished-archiving') {
      Notification.displaySuccessNotification('You updated your roster!')
    }
    navigate(Routes.dashboard())
  }

  const hideModal = () => setModal(null)

  const onChangeCleanUpCourses = () => setCleanUpCourses(!cleanUpCourses)

  const archiveProgressCB = useSafeCallbackExtraDeps(
    ({phase, persistence}: {phase: CleanUpPhaseT; persistence: typeof operationPersistence}) => {
      const result = (args: {
        archived: Array<UnmanagedCourseT>
        remaining: Array<UnmanagedCourseT>
        cancelOperation: () => void
      }) => {
        const {archived, remaining, cancelOperation} = args

        if (cancel) {
          persistence.remove(DUPLICATE_STUDENTS_KEY)
          cancelOperation()
        }

        invariant(
          phase.tag === 'start-archiving-courses' || phase.tag === 'archiving-courses',
          'Must be archiving courses in archive progress handler',
        )

        setPhase({
          tag: 'archiving-courses',
          unmanaged: phase.unmanaged,
          archived,
          remaining,
        })
      }

      return result
    },
    [cancel],
    {
      phase: {value: phase, comparator: isEqual},
      persistence: {value: operationPersistence, comparator: () => true},
    },
  )

  const handleArchiveProgress = useSafeCallbackExtraDeps<
    () => void,
    {
      phase: CleanUpPhaseT
      archiveProgressCB: ({
        archived,
        remaining,
        cancelOperation,
      }: {
        archived: Array<UnmanagedCourseT>
        remaining: Array<UnmanagedCourseT>
        cancelOperation: () => void
      }) => void
    }
  >(
    ({phase: _phase, archiveProgressCB}) =>
      () =>
        archiveProgressCB,
    [],
    {
      phase: {value: phase, comparator: isEqual},
      archiveProgressCB,
    },
  )

  useSafeEffectExtraDeps<{
    phase: CleanUpPhaseT
    schools: SchoolAttrs[]
    handleArchiveProgress: ({
      archived,
      remaining,
      cancelOperation,
    }: {
      archived: Array<UnmanagedCourseT>
      remaining: Array<UnmanagedCourseT>
      cancelOperation: () => void
    }) => void
    onFinish: () => void
    operationPersistence: typeof operationPersistence
  }>(
    ({phase, schools, handleArchiveProgress, onFinish, operationPersistence}) => {
      if (phase.tag === 'start-archiving-courses') {
        const {remaining} = phase
        const archiveUnmanagedCourses = async () => {
          try {
            await RosterApi.archiveUnmanagedCourses(
              remaining,
              (archived, remaining, cancelOperation) =>
                handleArchiveProgress({archived, remaining, cancelOperation}),
            )
            // There's no summary page after archiving, just redirect
            setPhase({tag: 'finished-archiving'})
          } catch (e) {
            logErrorAsUnhandled(new HttpError(e))
            Notification.displayErrorNotification(
              'Could not archive courses. Please try again later.',
            )
            // We could try to recover some progress from the intermediate
            // states, but starting from the beginning has better guarantees
            // since we may have partially succeeded somewhere
            setPhase({tag: 'start'})
          }
        }
        archiveUnmanagedCourses()
      }

      if (phase.tag === 'finished-archiving') {
        onFinish()
      }

      if (phase.tag === 'merging-duplicates') {
        const mergeDuplicateStudents = async () => {
          const schoolIds = schools.map(school => school.id)
          try {
            const location = await RosterApi.mergeDuplicateStudentsForSchools(schoolIds)
            setOperationLocation(location)
            operationPersistence.track(DUPLICATE_STUDENTS_KEY, location)
          } catch (e) {
            logErrorAsUnhandled(new HttpError(e))
            Notification.displayErrorNotification(
              'Could not merge duplicate students. Please try again later.',
            )
            setPhase({tag: 'start'})
          }
        }
        mergeDuplicateStudents()
      }
    },
    [phase.tag],
    {
      phase: equalExtra(phase),
      schools: equalExtra(schools),
      handleArchiveProgress,
      onFinish: staticExtra(onFinish),
      operationPersistence: staticExtra(operationPersistence),
    },
  )

  const renderSpinner = () => {
    switch (phase.tag) {
      case 'fetching-duplicates':
        return <DelayedSpinner text={t('CLEAN_UP_ROSTER_DELAYED_SPINNER_STUDENTS')} />
      case 'scanning-courses':
        return <DelayedSpinner text={t('CLEAN_UP_ROSTER_DELAYED_SPINNER_COURSES')} />
      case 'archiving-courses': {
        const {archived, remaining} = phase
        const description = calculateArchiveStatus(archived, remaining)
        return <DelayedSpinner text={description} />
      }
      default:
        return null
    }
  }

  const renderBody = () => {
    const {numUnmanagedStudents} = calculateUnmanagedStudents(schools, initialSummary)

    switch (phase.tag) {
      case 'start':
      case 'fetching-duplicates':
      case 'merging-duplicates':
      case 'scanning-courses':
        return (
          <CleanUpRoster
            onMerge={onMerge}
            cleanUpCourses={cleanUpCourses}
            onChange={onChangeCleanUpCourses}
            numSelfRosteredTeachers={numSelfRosteredTeachers}
            activeIdps={getActiveIdps(schools)}
            initialUnmanagedCourses={initialUnmanagedCourses}
            numUnmanagedStudents={numUnmanagedStudents}
          />
        )
      case 'finished-scanning':
        return (
          <ArchiveCourses
            unmanaged={phase.unmanaged}
            archivedCourses={[]}
            onReview={onReview}
            onArchive={() => {
              onArchive()
            }}
          />
        )
      case 'start-archiving-courses':
      case 'archiving-courses':
        return (
          <ArchiveCourses
            unmanaged={phase.unmanaged}
            archivedCourses={phase.archived}
            onReview={onReview}
            onArchive={() => {
              onArchive()
            }}
          />
        )
      case 'finished-merging':
        return <FinishedMerging unmanaged={phase.unmanaged} />
      case 'no-courses-found':
        return <NoCoursesFound unmanaged={phase.unmanaged} />
      case 'finished-archiving':
        return <div />
      default:
        return exhaustive(phase)
    }
  }

  const {teachersMissingEmailCount} = summary

  const downloadMissingEmailsCsv = () =>
    fancyClient
      .GET('/2/school-admins/me/teachers-missing-emails', {
        headers: {
          Accept: 'text/csv',
        },
        parseAs: 'blob',
        params: {
          query: {
            schoolIds: schools.map(s => s.id),
          },
        },
      })
      .then(extractDataAndHeaders)
      .then(({data, headers}) => downloadFile(data, headers, 'teachers-missing-emails.csv'))

  const missingEmailsAlert =
    teachersMissingEmailCount > 0 ? (
      <div className={teachersMissingEmailAlertWrapper}>
        <Alert type="warning">
          <SafeTrans
            i18nKey="CLEAN_UP_ROSTER_MISSING_EMAIL_ALERT_MESSAGE"
            values={{teachersMissingEmailCount}}
            components={{
              VerifyEmailLink: (
                <Link href="https://help2.renaissance.com/freckle/204644444" reactRouter={false} />
              ),
            }}
          />
          <Button
            style="link"
            className={downloadLink}
            leftIcon={faDownload}
            onClick={() => downloadMissingEmailsCsv()}
          >
            <SafeTrans i18nKey="CLEAN_UP_ROSTER_MISSING_EMAIL_CSV_DOWNALOAD_LINK" />
          </Button>
        </Alert>
      </div>
    ) : null

  const breadcrumbsConfig = {
    currentItemName: spanDangerous(t('CLEAN_UP_ROSTER_CLEAN_UP_BREADCRUMB')),
    path: [
      {
        url: Routes.dashboard(),
        name: t('CLEAN_UP_ROSTER_DASHBOARD_BREADCRUMB'),
      },
    ],
    reactRouter: true,
  }

  return (
    <>
      <PageWrapper
        title={t('CLEAN_UP_ROSTER_TITLE')}
        breadcrumbsConfig={breadcrumbsConfig}
        addPageWrapperContainerClass={pageWrapper}
      >
        {missingEmailsAlert}
        <RosterBody>{renderBody()}</RosterBody>
      </PageWrapper>

      {renderSpinner()}

      <PendingOperationWhenLocation
        operationLocation={operationLocation}
        onSuccess={onMergeDuplicateStudentsSuccess}
        onFailure={onMergeDuplicateStudentsFailure}
        onFinish={onMergeDuplicateStudentsFinish}
        i18nKeys={{
          title: 'MERGE_STUDENTS_PENDING_TITLE',
          heading: 'MERGE_STUDENTS_PENDING_HEADING',
          body: 'MERGE_STUDENTS_PENDING_BODY',
        }}
      />
      <CourseSummaryModal
        show={modal === 'course-summary'}
        onHide={hideModal}
        courses={phase.tag === 'finished-scanning' ? phase.remaining : []}
      />
      <ExitModal
        show={modal === 'exit-early'}
        yesText={t('CLEAN_UP_ROSTER_EXIT_MODAL_BUTTON_YES')}
        onCancel={hideModal}
        onLeave={onFinish}
      />
      <ConfirmMergeModal
        show={modal === 'confirm-dedup-merge'}
        onConfirm={() => {
          onMergeConfirmed()
        }}
        onCancel={hideModal}
      />
    </>
  )
}

function calculateArchiveStatus(
  archived: Array<UnmanagedCourseT>,
  remaining: Array<UnmanagedCourseT>,
): string {
  const numArchived = archived.length
  const numRemaining = remaining.length
  return `Archived ${numArchived}/${numRemaining + numArchived} courses`
}
